<script lang="ts">
import type { CSSProperties, HTMLAttributes } from 'vue'

export type TextareaSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface TextareaProps {
  class?: HTMLAttributes['class']
  defaultValue?: string | number
  modelValue?: string | number
  size?: TextareaSize
  error?: boolean | string
  /**
   * Grow vertically to fit content via CSS `field-sizing: content`. Default `true`.
   * Falls back to native `:rows` on browsers without support (Safari < 17.4, Firefox).
   */
  enableResize?: boolean
  /**
   * Cap auto-grow height at this many rows of body text. Default `3`. Ignored when `enableResize` is false.
   */
  maxRows?: number
}
</script>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { cva } from 'class-variance-authority'
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<TextareaProps>(), {
  size: 'md',
  enableResize: true,
  maxRows: 3,
})

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})

// Total vertical padding per size (matches the py-* utility used in the cva size variant).
const verticalPaddingRem: Record<TextareaSize, number> = {
  xs: 0.5,
  sm: 0.75,
  md: 1,
  lg: 1.25,
  xl: 1.5,
}

const autoSizeStyle = computed<CSSProperties | undefined>(() => {
  if (!props.enableResize) return undefined
  return { maxHeight: `calc(${props.maxRows} * 1lh + ${verticalPaddingRem[props.size]}rem)` }
})

const textareaVariants = cva(
  [
    'flex w-full rounded-lg bg-white',
    'border-0 ring-1 ring-inset ring-neutral-600',
    'text-primal font-display',
    'placeholder:text-placeholder',
    'outline-0',
    'focus:ring-inset focus:ring-2 focus:ring-primary-500',
    '[&:not(:focus):not(:read-only):hover]:bg-neutral-600/10',
    'read-only:bg-neutral-600/30 read-only:ring-neutral-600/60 read-only:text-gray-600',
    'disabled:cursor-not-allowed disabled:bg-neutral-600/30 disabled:ring-0 disabled:text-neutral-900/30',
    'resize-none shadow-none overflow-auto',
    'scrollbar-thin [scrollbar-color:var(--color-neutral-600)_transparent]',
  ].join(' '),
  {
    variants: {
      size: {
        xs: 'min-h-6 px-2 py-1 text-xs',
        sm: 'min-h-8 px-2 py-1.5 text-xs',
        md: 'min-h-10 px-2 py-2 text-base',
        lg: 'min-h-[52px] px-3 py-2.5 text-base',
        xl: 'min-h-[60px] px-4 py-3 text-lg',
      },
      error: {
        true: 'ring-error!',
      },
      enableResize: {
        true: 'field-sizing-content',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)
</script>

<template>
  <textarea
    v-model="modelValue"
    :aria-invalid="!!props.error"
    :class="
      cn(textareaVariants({ size: props.size, error: !!props.error, enableResize: props.enableResize }), props.class)
    "
    :style="autoSizeStyle"
  />
</template>
